import { useState, useContext, useEffect } from "react"
import Header from "../../../layouts/Header"
import BackIcon from "../../../components/BackIcon"
import { useNavigate } from "react-router-dom"
import {
  Box,
  Paper,
  TextField,
  Tooltip,
  IconButton,
  Grid,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  Dialog,
  DialogContent,
  TableContainer,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Autocomplete,
} from "@mui/material"
import { createTheme, ThemeProvider } from "@mui/material/styles"
import CustomBackdrop from "../../../components/CustomBackdrop"
import { useMutation, useQuery, api, showWarning } from "../../../api"
import { GlobalContext } from "../../../contexts/GlobalContext"
import getIconComponent from "../../utils/getIconComponent"
import DeleteIcon from "@mui/icons-material/Delete"
import AddCircleIcon from "@mui/icons-material/AddCircle"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import AssignmentIcon from "@mui/icons-material/Assignment"
import ScaleIcon from "@mui/icons-material/Scale"
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong"
import PersonAddIcon from "@mui/icons-material/PersonAdd"

// IMPORTAMOS EL HELPER DE IMPRESIÓN Y EL MODAL DEL PROVEEDOR
import { handlePrintPesajeTicket, handlePrintPesajeTicketBluetooth } from "../utils/printPesajeTicketHelper"
import CrearProveedoresCreaRapi from "../utils/CrearProveedoresCreaRapi"
import { useQueryClient } from "@tanstack/react-query"

const theme = createTheme({
  palette: { primary: { main: "#196C87" }, secondary: { main: "#2E7D32" } },
})

const StyledRoot = {
  width: "100%",
  maxWidth: "1200px",
  margin: "0 auto",
  padding: "20px",
  backgroundColor: "#f5f7fa",
  borderRadius: "12px",
  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
}

const accordionStyles = {
  mb: 2,
  border: "1px solid",
  borderColor: "divider",
  borderRadius: "8px !important",
  boxShadow: "none",
  "&:before": { display: "none" },
}

const CrearPesajes = () => {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const { selectedMenuInfo, infoHome } = useContext(GlobalContext)

  const [openProvModal, setOpenProvModal] = useState(false)

  const [formData, setFormData] = useState({
    loccodigo: localStorage.getItem("loccodigo") || "01",
    balcodigo: "",
    procodigo: "",
    pronombre: "",
    invcodigo: "",
    artcodigo: "",
    artdescri: "",
    pesoprecio: "0.00000000", // Inicializado en 8 decimales
    pesohumeporcen: 0.0,
    impid: "",
    pesoretenporcen: 0.0,
    pesocantbruta: 0.0,
    pesohumecantidad: 0.0,
    pesocantfinal: 0.0,
    pesovalbruto: 0.0,
    pesoretenvalor: 0.0,
    pesovaltot: 0.0,
  })

  const [detalles, setDetalles] = useState([])

  const { data: rawInitialData, isLoading: isLoadingInit } = useQuery({
    queryKey: ["getInitialDataPesajes", formData.loccodigo],
    queryFn: async () => {
      try {
        const response = await api.post("/Pesajes/getInitialDataPesajes", { loccodigo: formData.loccodigo })
        const resData = response?.data
        if (resData?.balanzas) return resData
        if (resData?.data?.balanzas) return resData.data
        if (resData?.data?.data?.balanzas) return resData.data.data
        return { balanzas: [], proveedores: [], articulos: [] }
      } catch (error) {
        return { balanzas: [], proveedores: [], articulos: [] }
      }
    },
    enabled: !!formData.loccodigo,
    refetchOnWindowFocus: false,
  })

  const listaBalanzas = (rawInitialData?.balanzas || []).map((i) => ({
    id: i.balcodigo,
    label: `${i.balcodigo} - ${i.baldescri}`,
  }))
  const listaProveedores = (rawInitialData?.proveedores || []).map((i) => ({
    id: i.procodigo,
    label: `${i.procodigo} - ${i.pronombre}`,
    nombre: i.pronombre,
  }))
  const listaArticulos = (rawInitialData?.articulos || []).map((i) => ({
    id: i.artcodigo,
    label: `${i.artcodigo} - ${i.artdescri}`,
    invcodigo: i.invcodigo,
    descri: i.artdescri,
    precio: i.artprecventa6,
  }))

  const { data: rawRetenciones, isFetching: isFetchingRet } = useQuery({
    queryKey: ["getRetencionesProv", formData.procodigo],
    queryFn: async () => {
      try {
        const response = await api.post("/Pesajes/getRetencionesProv", { procodigo: formData.procodigo })
        const resData = response?.data
        if (Array.isArray(resData)) return resData
        if (Array.isArray(resData?.data)) return resData.data
        if (Array.isArray(resData?.data?.data)) return resData.data.data
        return []
      } catch (error) {
        return []
      }
    },
    enabled: !!formData.procodigo,
    refetchOnWindowFocus: false,
  })

  const listaRetenciones = (rawRetenciones || []).map((i) => ({
    id: i.impid,
    label: `${i.impid} - ${i.impdescri}`,
    porcent: i.impporcent,
  }))

  useEffect(() => {
    const brutas = detalles.reduce((acc, curr) => acc + (parseFloat(curr.pesocantidad) || 0), 0)
    const humedad = brutas * ((parseFloat(formData.pesohumeporcen) || 0) / 100)
    const netas = brutas - humedad

    const brutoUsd = netas * (parseFloat(formData.pesoprecio) || 0)
    const retencionUsd = brutoUsd * ((parseFloat(formData.pesoretenporcen) || 0) / 100)
    const totalUsd = brutoUsd - retencionUsd

    setFormData((prev) => ({
      ...prev,
      pesocantbruta: brutas.toFixed(2),
      pesohumecantidad: humedad.toFixed(2),
      pesocantfinal: netas.toFixed(2),
      pesovalbruto: brutoUsd.toFixed(2),
      pesoretenvalor: retencionUsd.toFixed(2),
      pesovaltot: totalUsd.toFixed(2),
    }))
  }, [detalles, formData.pesohumeporcen, formData.pesoprecio, formData.pesoretenporcen])

  const { mutateAsync: SaveCreacion, isPending: isSaving } = useMutation({
    queryKey: ["isCreatingPesaje"],
    fn: async (dataPayload) => await api.post("/Pesajes/createPesajes", dataPayload),
    showError: "modal",
    showSuccess: "toast",
    onSuccess: async (response) => {
      const responseDataString = String(response?.data?.data || response?.data || "")
      const match = responseDataString.match(/Pesaje ([A-Z0-9]+) generado/i)
      const nuevoPesoid = match ? match[1] : "NUEVO"

      try {
        // 1. Intentar Imprimir por Bluetooth Directo (Android/Chrome/Windows)
        await handlePrintPesajeTicketBluetooth({ ...formData, pesoid: nuevoPesoid }, detalles, infoHome)
      } catch (error) {
        // 2. Fallback: Si cancela, no enlaza la impresora, o está usando iOS Safari,
        // levantamos el PDF tradicional para que el usuario no se quede sin comprobante.
        handlePrintPesajeTicket({ ...formData, pesoid: nuevoPesoid }, detalles, infoHome)
      }

      // Regresar al listado después de imprimir
      setTimeout(() => {
        navigate(-1)
      }, 500)
    },
  })

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: typeof value === "string" ? value.toUpperCase() : value }))
  }

  const handleDetailChange = (idx, value) => {
    const n = [...detalles]
    n[idx].pesocantidad = value
    setDetalles(n)
  }

  const handleSubmit = async (e) => {
    if (e) e.preventDefault()
    if (!formData.balcodigo || !formData.procodigo || !formData.artcodigo) {
      return showWarning("Balanza, Proveedor y Artículo son obligatorios.")
    }
    const detallesFiltrados = detalles.filter((d) => parseFloat(d.pesocantidad) > 0)
    if (detallesFiltrados.length === 0) return showWarning("Debe registrar valores mayores a 0 en el pesaje.")

    await SaveCreacion({ ...formData, detalles: detallesFiltrados })
  }

  const action = selectedMenuInfo?.data?.barraAcciones?.find((a) => a.acccaption === "GRABAR")

  return (
    <ThemeProvider theme={theme}>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <BackIcon />
        <Box sx={{ mb: 2 }}>
          {action && (
            <Tooltip title={action.acccaption}>
              <IconButton
                onClick={handleSubmit}
                disabled={isSaving || isLoadingInit}
                sx={{ border: "1px solid #ddd", bgcolor: "white" }}
              >
                {getIconComponent(action.accnameicono, action.acctipoico)}
              </IconButton>
            </Tooltip>
          )}
        </Box>
        <div style={{ display: "flex", justifyContent: "center", mb: "30px", fontSize: "25px" }}>
          <b>Registro de Nuevo Pesaje</b>
        </div>

        <CustomBackdrop isLoading={isSaving || isLoadingInit} />

        <Dialog open={openProvModal} onClose={() => setOpenProvModal(false)} maxWidth="md" fullWidth>
          <DialogContent sx={{ p: 0 }}>
            <CrearProveedoresCreaRapi
              isModal={true}
              onClose={() => setOpenProvModal(false)}
              onProveedorCreado={async (nuevoProcodigo) => {
                setOpenProvModal(false)
                // Forzamos al query client a ir al backend a buscar la data fresca de proveedores
                await qc.refetchQueries({ queryKey: ["getInitialDataPesajes", formData.loccodigo] })
                setFormData((prev) => ({ ...prev, procodigo: nuevoProcodigo }))
              }}
            />
          </DialogContent>
        </Dialog>

        <Box sx={StyledRoot} component="form" onSubmit={handleSubmit}>
          <Accordion defaultExpanded sx={accordionStyles}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <AssignmentIcon color="primary" fontSize="small" />
                <Typography variant="subtitle2" fontWeight="bold" color="primary">
                  DATOS GENERALES
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={3}>
                  <Autocomplete
                    options={listaBalanzas}
                    getOptionLabel={(o) => o.label || ""}
                    value={listaBalanzas.find((c) => c.id === formData.balcodigo) || null}
                    onChange={(e, v) => handleInputChange("balcodigo", v ? v.id : "")}
                    renderInput={(p) => <TextField {...p} label="Balanza *" InputLabelProps={{ shrink: true }} />}
                  />
                </Grid>

                <Grid item xs={12} sm={9}>
                  <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <Autocomplete
                      sx={{ flexGrow: 1 }}
                      options={listaProveedores}
                      getOptionLabel={(o) => o.label || ""}
                      value={listaProveedores.find((c) => c.id === formData.procodigo) || null}
                      onChange={(e, v) => {
                        setFormData((prev) => ({
                          ...prev,
                          procodigo: v ? v.id : "",
                          pronombre: v ? v.nombre : "",
                          impid: "",
                          pesoretenporcen: 0.0,
                        }))
                      }}
                      renderInput={(p) => <TextField {...p} label="Proveedor *" InputLabelProps={{ shrink: true }} />}
                    />
                    <Button
                      variant="outlined"
                      color="primary"
                      startIcon={<PersonAddIcon />}
                      onClick={() => setOpenProvModal(true)}
                      sx={{ height: "56px", whiteSpace: "nowrap" }}
                    >
                      NUEVO
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          <Accordion defaultExpanded sx={accordionStyles}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <ReceiptLongIcon color="primary" fontSize="small" />
                <Typography variant="subtitle2" fontWeight="bold" color="primary">
                  PRODUCTO Y CONDICIONES
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={5}>
                  <Autocomplete
                    options={listaArticulos}
                    getOptionLabel={(o) => o.label || ""}
                    value={listaArticulos.find((c) => c.id === formData.artcodigo) || null}
                    onChange={(e, v) => {
                      // Formateo seguro para evitar "0E-8" o "null"
                      let precioFormateado = "0.00000000"
                      if (v && v.precio !== undefined && v.precio !== null) {
                        precioFormateado = Number(v.precio).toFixed(8)
                      }

                      setFormData((prev) => ({
                        ...prev,
                        artcodigo: v ? v.id : "",
                        artdescri: v ? v.descri : "",
                        invcodigo: v ? v.invcodigo : "",
                        pesoprecio: precioFormateado,
                      }))
                    }}
                    renderInput={(p) => <TextField {...p} label="Artículo *" InputLabelProps={{ shrink: true }} />}
                  />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <TextField
                    type="number"
                    fullWidth
                    label="Precio Sugerido ($)"
                    inputProps={{ step: "0.00000001" }}
                    value={formData.pesoprecio}
                    onChange={(e) => handleInputChange("pesoprecio", e.target.value)}
                    onFocus={(e) => e.target.select()} // EVENTO SOLICITADO
                  />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <TextField
                    type="number"
                    fullWidth
                    label="% Humedad"
                    inputProps={{ step: "0.01" }}
                    value={formData.pesohumeporcen}
                    onChange={(e) => handleInputChange("pesohumeporcen", e.target.value)}
                    onFocus={(e) => e.target.select()} // EVENTO SOLICITADO
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Autocomplete
                    options={listaRetenciones}
                    getOptionLabel={(o) => o.label || ""}
                    value={listaRetenciones.find((c) => c.id === formData.impid) || null}
                    onChange={(e, v) => {
                      setFormData((prev) => ({ ...prev, impid: v ? v.id : "", pesoretenporcen: v ? v.porcent : 0.0 }))
                    }}
                    loading={isFetchingRet}
                    renderInput={(p) => (
                      <TextField {...p} label="Retención Aplicable" InputLabelProps={{ shrink: true }} />
                    )}
                  />
                </Grid>
              </Grid>

              <Box sx={{ mt: 3, p: 2, bgcolor: "#e8f4f8", borderRadius: 2, border: "1px solid #b3d4e0" }}>
                <Typography variant="subtitle2" color="primary" mb={2}>
                  <b>RESUMEN DE LIQUIDACIÓN</b>
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={2}>
                    <Typography variant="caption" color="textSecondary">
                      Lbs Brutas
                    </Typography>
                    <Typography fontWeight="bold">{formData.pesocantbruta}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <Typography variant="caption" color="error">
                      (-) Lbs Humedad
                    </Typography>
                    <Typography fontWeight="bold" color="error">
                      {formData.pesohumecantidad}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <Typography variant="caption" color="primary">
                      (=) Lbs Netas
                    </Typography>
                    <Typography fontWeight="bold" color="primary">
                      {formData.pesocantfinal}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <Typography variant="caption" color="textSecondary">
                      Subtotal USD
                    </Typography>
                    <Typography fontWeight="bold">${formData.pesovalbruto}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <Typography variant="caption" color="error">
                      (-) Retención USD
                    </Typography>
                    <Typography fontWeight="bold" color="error">
                      ${formData.pesoretenvalor}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <Typography variant="caption" color="primary">
                      (=) TOTAL A PAGAR
                    </Typography>
                    <Typography variant="h6" fontWeight="bold" color="primary">
                      ${formData.pesovaltot}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </AccordionDetails>
          </Accordion>

          <Accordion defaultExpanded sx={accordionStyles}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <ScaleIcon color="primary" fontSize="small" />
                <Typography variant="subtitle2" fontWeight="bold" color="primary">
                  INGRESOS DE BÁSCULA
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ mb: 2, display: "flex", justifyContent: "flex-start" }}>
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<AddCircleIcon />}
                  onClick={() => setDetalles([...detalles, { pesocantidad: "" }])}
                >
                  Añadir Peso
                </Button>
              </Box>
              <TableContainer component={Paper} variant="outlined" sx={{ maxWidth: "500px" }}>
                <Table size="small">
                  <TableHead sx={{ bgcolor: "#f5f5f5" }}>
                    <TableRow>
                      <TableCell align="center" sx={{ width: "80px" }}>
                        <b># Secuencia</b>
                      </TableCell>
                      <TableCell align="center">
                        <b>Cantidad (Libras)</b>
                      </TableCell>
                      <TableCell align="center" sx={{ width: "80px" }}>
                        <b>Quitar</b>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {detalles?.map((row, idx) => (
                      <TableRow key={idx}>
                        <TableCell align="center" sx={{ bgcolor: "#fafafa" }}>
                          {idx + 1}
                        </TableCell>
                        <TableCell padding="none" sx={{ px: 2 }}>
                          <TextField
                            type="number"
                            variant="standard"
                            fullWidth
                            inputProps={{ style: { textAlign: "center" }, step: "0.01" }}
                            value={row.pesocantidad}
                            onChange={(e) => handleDetailChange(idx, e.target.value)}
                          />
                        </TableCell>
                        <TableCell align="center" padding="none">
                          <IconButton
                            color="error"
                            size="small"
                            onClick={() => {
                              const n = [...detalles]
                              n.splice(idx, 1)
                              setDetalles(n)
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                    {detalles.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={3} align="center" sx={{ py: 3, color: "text.secondary" }}>
                          Haga clic en 'Añadir Peso' para registrar una nueva lectura de báscula.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>
        </Box>
      </div>
    </ThemeProvider>
  )
}

export default CrearPesajes
